import { useEffect, useRef } from 'react';

import { AuditLogger } from 'src/utils/audit-logger';

const auditLogger = AuditLogger.getInstance();
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5분
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = ['mousemove', 'keydown', 'click', 'scroll'];

/**
 * 대상 사용자에 대해 활동 기반 하트비트 로그를 기록하는 훅.
 *
 * - 5분 간격으로 활동 여부 체크 → 활동 있으면 '하트비트 (활성)' 기록
 * - 5분 동안 활동 없으면 '자리비움' 기록
 * - 자리비움 상태에서 활동 감지 시 즉시 '자리비움 해제' 기록
 */
function useActivityHeartbeat(uid: string | undefined, targetUids: string[] | undefined): void {
  const isActive = useRef(false);
  const isAway = useRef(false);

  useEffect(() => {
    if (!uid || !targetUids || !targetUids.includes(uid)) {
      return;
    }

    const markActive = () => {
      isActive.current = true;
      if (isAway.current) {
        isAway.current = false;
        auditLogger.log({
          action: 'SYNC',
          entity: 'session',
          entityId: uid,
          summary: '자리비움 해제',
        });
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, markActive, { passive: true });
    });

    const intervalId = setInterval(() => {
      if (isActive.current) {
        auditLogger.log({
          action: 'SYNC',
          entity: 'session',
          entityId: uid,
          summary: '하트비트 (활성)',
        });
        isActive.current = false;
      } else if (!isAway.current) {
        isAway.current = true;
        auditLogger.log({
          action: 'SYNC',
          entity: 'session',
          entityId: uid,
          summary: '자리비움',
        });
      }
    }, HEARTBEAT_INTERVAL);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, markActive);
      });
      clearInterval(intervalId);
    };
  }, [uid, targetUids]);
}

export default useActivityHeartbeat;
